import {
  Box,
  Button,
  Flex,
  FormLabel,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  Tooltip,
  VStack,
  Input,
  InputGroup,
} from '@chakra-ui/react';
import { Formik, Form, getIn } from 'formik';
import _ from 'lodash';
import React from 'react';
import { pipe, map, filter } from 'remeda';

import {
  utils as commonUtils,
  error as commonError,
  finleyAssert,
} from '@finley-technologies/common';
import {
  CreditAgreement,
  Deliverable,
  Notifications,
  Util,
} from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import {
  CheckboxFormField,
  InputFormField,
  FormFieldLabel,
  TextareaFormField,
} from 'packages/common-frontend/components/FormField';
import { IsolatedMultiSelect } from 'packages/common-frontend/components/MultiSelect';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import { formatBasePropertyKey } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FiInfo } from 'react-icons/fi';

const createDeliverableEdges = (
  reportingDeliverables: CreditAgreement.DomainEntity.DEPRECATED.DeliverablePropertyWithEntities[],
): [string, string][] => {
  const deliverableEdges: [string, string][] = [];
  return _.reduce(
    reportingDeliverables,
    (acc: [string, string][], { _config_: { configKey, dependencies } }) => {
      if (_.isEmpty(dependencies)) {
        return acc;
      }
      const newEdges: [string, string][] = _.map(
        dependencies,
        (dependency: string) => [configKey, dependency],
      );
      return [...acc, ...newEdges];
    },
    deliverableEdges,
  );
};

const createConfigShell = ({
  name,
  deliverableType = Deliverable.v3DEPRECATED.DeliverableType.Other,
  frequency = Util.FrequencyPeriod.Month,
  dependencies = [],
  partyPermissions = [],
  preUploadNotificationProcesses = [
    {
      notificationType:
        Notifications.BorrowerEmailType.deliverablesResponsiblePartyReminder,
    },
    {
      notificationType:
        Notifications.BorrowerEmailType
          .deliverablesResponsiblePartyStatusUpdate,
    },
  ],
  postUploadNotificationProcesses = [
    {
      notificationType:
        Notifications.BorrowerEmailType
          .deliverablesResponsiblePartySubmitSubmission,
    },
  ],
}: {
  name: string;
  deliverableType?: Deliverable.v3DEPRECATED.DeliverableType;
  frequency?: Util.FrequencyPeriod;
  dependencies?: string[]; // array of configKeys
  partyPermissions?: CreditAgreement.Config.DeliverablePropertyDEPRECATED.PartyPermissionWithEntity[];
  preUploadNotificationProcesses?: CreditAgreement.Config.DeliverablePropertyDEPRECATED.PreUploadNotificationProcess[];
  postUploadNotificationProcesses?: CreditAgreement.Config.DeliverablePropertyDEPRECATED.PostUploadNotificationProcess[];
}): CreditAgreement.Config.DeliverablePropertyDEPRECATED.ConfigWithEntities => ({
  configKey: _.camelCase(name),
  deliverableType,
  frequency,
  dependencies,
  partyPermissions,
  preUploadNotificationProcesses,
  // @jendevelops TODO: add in post upload notification process when cap provider submission + borrower submission email is implemented
  postUploadNotificationProcesses,
});

const createDeliverablePropertyFileShell = ({
  name = 'placeholder',
  fileTypes = [],
  responsibleParties = [],
  dueDates = [],
  _config_,
}: {
  name?: string;
  fileTypes?: string[];
  responsibleParties?: { entity: { id: string; name: string; slug: string } }[];
  dueDates?: Util.FullDateStr[];
  _config_?: CreditAgreement.Config.DeliverablePropertyDEPRECATED.ConfigWithEntities;
}): CreditAgreement.DomainEntity.DEPRECATED.DeliverableFilePropertyWithEntities => {
  const config = _config_ || createConfigShell({ name });
  return {
    name,
    type: CreditAgreement.DomainEntity.PropertyType.DeliverableProperty,
    value: 'file' as const,
    fileTypes,
    responsibleParties,
    dueDates,
    _config_: config,
    _isWrittenIn_: false,
    _note_: '',
  };
};

export const createDeliverablePropertyNonFileShell = ({
  name = 'placeholder',
  value = 'string',
  responsibleParties = [],
  dueDates = [],
  _config_,
}: {
  name?: string;
  value: Exclude<Deliverable.v3DEPRECATED.DeliverableValueType, 'file'>;
  responsibleParties?: { entity: { id: string; name: string; slug: string } }[];
  dueDates?: Util.FullDateStr[];
  _config_?: CreditAgreement.Config.DeliverablePropertyDEPRECATED.ConfigWithEntities;
}): CreditAgreement.DomainEntity.DEPRECATED.DeliverableBasePropertyWithEntities => {
  const config = _config_ || createConfigShell({ name });
  return {
    name,
    type: CreditAgreement.DomainEntity.PropertyType.DeliverableProperty,
    value,
    responsibleParties,
    dueDates,
    _config_: config,
    _isWrittenIn_: false,
    _note_: '',
  };
};

export const EditReportingDeliverableModal: React.FC<{
  basePropertyKey: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  wipConfig: WipConfig;
  reportingDeliverable?: CreditAgreement.DomainEntity.DEPRECATED.DeliverablePropertyWithEntities;
  onSubmit: (props: any) => void;
}> = ({
  basePropertyKey,
  isOpen,
  setIsOpen,
  reportingDeliverable,
  wipConfig: { formProps, organizationMap },
  onSubmit,
}) => {
  const currentProperty: CreditAgreement.DomainEntity.DEPRECATED.DeliverablePropertyWithEntities =
    reportingDeliverable || createDeliverablePropertyFileShell({});

  const reportingDeliverables: CreditAgreement.DomainEntity.DEPRECATED.DeliverablePropertyWithEntities[] = getIn(
    formProps.values,
    'reportingDeliverables',
  );
  const deliverableEdges: [string, string][] = createDeliverableEdges(
    reportingDeliverables,
  );
  // create party options from parties configured in facility mechanics
  const parties = getIn(formProps.values, 'facilityMechanics.parties');
  const finley = _.find(
    organizationMap,
    ({ entity: { slug } }) => slug === 'finley',
  );

  finleyAssert(
    finley,
    new commonError.FinleyError('Finley not found in organizationMap', {
      metadata: { logMeta: { reportingDeliverable } },
    }),
  );

  const partyOptions: { value: string; label: string }[] = _.reduce(
    parties,
    (
      acc: { value: string; label: string }[],
      partyArray: { entity: { id: string; name: string; slug: string } }[],
    ) => {
      if (_.isEmpty(partyArray)) return acc;
      const newParties = _.compact(
        _.map(partyArray, ({ entity: { id, name } }) =>
          _.includes(_.map(acc, 'value'), id)
            ? null
            : {
                value: id,
                label: name,
              },
        ),
      );

      const newOptions = [...acc, ...newParties];
      return newOptions;
    },
    [{ label: finley.entity.name, value: finley.entity.id }],
  );

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="2xl">
      <ModalOverlay />
      <ModalContent padding="20px">
        <ModalHeader>Edit Property</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex flexDirection="row" alignItems="flex-start" mb={5}>
            <FormLabel w="60" mb="0">
              Property
            </FormLabel>
            <Box width="100%">
              <Text width="sm">{formatBasePropertyKey(basePropertyKey)}</Text>
            </Box>
          </Flex>
          <Formik
            onSubmit={(props) => {
              // set to change setFieldValue key
              onSubmit(props);
              setIsOpen(false);
            }}
            initialValues={{
              ...currentProperty,
            }}
          >
            {(localFormProps) => {
              const validDependencies = pipe(
                reportingDeliverables,
                map(
                  ({ _config_: { configKey: configKeyOption } }) =>
                    configKeyOption,
                ),
                filter((currentConfigKey) => {
                  const sort = commonUtils.sortTopologically([
                    ...deliverableEdges,
                    [
                      localFormProps.values._config_.configKey,
                      currentConfigKey,
                    ],
                  ]);
                  return (
                    _.isEmpty(sort.cycles) &&
                    currentConfigKey !==
                      localFormProps.values._config_.configKey
                  );
                }),
              );
              return (
                <Form onSubmit={localFormProps.handleSubmit}>
                  <VStack spacing={5}>
                    <Flex
                      width="100%"
                      flexDirection="row"
                      alignItems="flex-start"
                    >
                      <FormFieldLabel label="Name" labelWidth="60" />
                      <InputGroup w="100%">
                        <Input
                          type="text"
                          data-testid="name"
                          name="name"
                          value={localFormProps.values.name}
                          onChange={(e) => {
                            const value = e.target.value;
                            localFormProps.setFieldValue('name', value);
                            localFormProps.setFieldValue(
                              '_config_.configKey',
                              _.camelCase(value),
                            );
                          }}
                          onBlur={formProps.handleBlur}
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    ={' '}
                    <Flex width="100%">
                      <FormFieldLabel
                        labelWidth="60"
                        label="Responsible Parties"
                      />
                      <InputGroup w="100%">
                        <IsolatedMultiSelect<string>
                          initialSelections={_.map(
                            localFormProps.values.responsibleParties,
                            ({ entity: { id } }) => id,
                          )}
                          onApplyOptions={(value) => {
                            const newResponsibleParties = _.map(value, (id) => {
                              const org = _.find(
                                organizationMap,
                                ({ entity: { id: orgId } }) => orgId === id,
                              );
                              if (!org) {
                                return null;
                              }
                              return { entity: org.entity };
                            });
                            localFormProps.setFieldValue(
                              'responsibleParties',
                              _.compact(newResponsibleParties),
                            );
                          }}
                          options={partyOptions}
                          titleText={
                            _.isEmpty(localFormProps.values.responsibleParties)
                              ? 'Select responsible parties'
                              : `${
                                  localFormProps.values.responsibleParties
                                    .length
                                } responsible part${
                                  localFormProps.values.responsibleParties
                                    .length > 1
                                    ? 'ies'
                                    : 'y'
                                } selected`
                          }
                          applyButtonText="Apply"
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    <Flex w="100%">
                      <FormFieldLabel
                        labelWidth="60"
                        label="Value Type"
                        tooltip={
                          <Tooltip label="Only file based deliverables are able to be configured at this time">
                            <Icon fontSize="lg">
                              <FiInfo />
                            </Icon>
                          </Tooltip>
                        }
                      />
                      <InputGroup w="100%">
                        <Select<Deliverable.v3DEPRECATED.DeliverableValueType>
                          value={localFormProps.values.value}
                          onChange={(value) => {
                            localFormProps.setFieldValue('value', value);
                            if (value === 'file') {
                              localFormProps.setFieldValue('fileTypes', []);
                            } else {
                              localFormProps.setFieldValue(
                                'fileTypes',
                                undefined,
                              );
                            }
                          }}
                          options={formatSelectOptions(
                            _.filter(
                              Deliverable.v3DEPRECATED.DeliverableValueTypes,
                              (type) => ['file', 'noSubmission'].includes(type),
                            ),
                            'sentence',
                          )}
                          placeholderLabel="Select report type"
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    {localFormProps.values.value === 'file' && (
                      <Flex width="100%">
                        <FormFieldLabel
                          labelWidth="60"
                          label="Allowed File Types"
                        />
                        <InputGroup w="100%">
                          <IsolatedMultiSelect
                            initialSelections={localFormProps.values.fileTypes}
                            onApplyOptions={(value: string[]) => {
                              localFormProps.setFieldValue('fileTypes', value);
                            }}
                            options={formatSelectOptions(
                              Deliverable.v3DEPRECATED.DeliverableFileTypes,
                            )}
                            titleText={
                              _.isEmpty(localFormProps.values.fileTypes)
                                ? 'Select allowed file types'
                                : `${
                                    localFormProps.values.fileTypes.length
                                  } file type${
                                    localFormProps.values.fileTypes.length > 1
                                      ? 's'
                                      : ''
                                  } selected`
                            }
                            applyButtonText="Apply"
                            width="sm"
                          />
                        </InputGroup>
                      </Flex>
                    )}
                    <InputFormField
                      propName="section"
                      label="Section"
                      formProps={localFormProps}
                      value={localFormProps.values.section}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                      alignItems="flex-end"
                    />
                    <TextareaFormField
                      propName="description"
                      label="Description"
                      formProps={localFormProps}
                      value={localFormProps.values.description}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                      alignItems="flex-end"
                      inputHeight="30"
                    />
                    <TextareaFormField
                      propName="_note_"
                      label="Note"
                      formProps={localFormProps}
                      value={localFormProps.values._note_}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                      alignItems="flex-end"
                      inputHeight="30"
                    />
                    <CheckboxFormField
                      propName="_isWrittenIn_"
                      label="Is Written In"
                      formProps={localFormProps}
                      value={localFormProps.values._isWrittenIn_}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                    />
                    <Flex width="100%" alignItems="center">
                      <Text textAlign="left" fontWeight="bold" mr="5px">
                        Config
                      </Text>
                      <Tooltip
                        hasArrow
                        placement="right"
                        label="Configs are not displayed to the user and are only used for internal processes"
                        flexGrow={1}
                      >
                        <Icon>
                          <FiInfo size="24px" />
                        </Icon>
                      </Tooltip>
                    </Flex>
                    <InputFormField
                      propName="_config_.configKey"
                      value={localFormProps.values._config_.configKey}
                      label="Config Key"
                      formProps={localFormProps}
                      flexDirection="row"
                      alignItems="flex-end"
                      inputWidth="sm"
                      labelWidth="60"
                    />
                    <Flex width="100%">
                      <FormFieldLabel
                        labelWidth="60"
                        label="Deliverable Type"
                      />
                      <InputGroup w="100%">
                        <Select<Deliverable.v3DEPRECATED.DeliverableType>
                          value={localFormProps.values._config_.deliverableType}
                          onChange={(value) => {
                            localFormProps.setFieldValue(
                              '_config_.deliverableType',
                              value,
                            );
                          }}
                          options={formatSelectOptions(
                            Deliverable.v3DEPRECATED.DeliverableType,
                            'sentence',
                          )}
                          placeholderLabel="Select deliverableType type"
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    <Flex width="100%">
                      <FormFieldLabel labelWidth="60" label="Frequency" />
                      <InputGroup w="100%">
                        <Select<Util.FrequencyPeriod>
                          value={localFormProps.values._config_.frequency}
                          onChange={(value) => {
                            localFormProps.setFieldValue(
                              '_config_.frequency',
                              value,
                            );
                          }}
                          options={formatSelectOptions(
                            _.values(Util.FrequencyPeriod),
                            'sentence',
                          )}
                          placeholderLabel="Select deliverableType type"
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    <Flex width="100%">
                      <FormFieldLabel labelWidth="60" label="Dependencies" />
                      <InputGroup w="100%">
                        <IsolatedMultiSelect<string>
                          initialSelections={
                            localFormProps.values._config_.dependencies
                          }
                          onApplyOptions={(value) => {
                            localFormProps.setFieldValue(
                              '_config_.dependencies',
                              value,
                            );
                          }}
                          options={formatSelectOptions(
                            validDependencies,
                            'sentence',
                          )}
                          titleText={
                            _.isEmpty(
                              localFormProps.values._config_.dependencies,
                            )
                              ? 'Select dependencies'
                              : `${
                                  localFormProps.values._config_.dependencies
                                    .length
                                } dependenc${
                                  localFormProps.values._config_.dependencies
                                    .length > 1
                                    ? 'ies'
                                    : 'y'
                                } selected`
                          }
                          applyButtonText="Apply"
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    {/* @jendevelops TODO Add medium type */}
                    <Flex width="100%">
                      <FormFieldLabel
                        labelWidth="60"
                        label="Pre-upload Notifications"
                      />
                      <InputGroup w="100%">
                        <IsolatedMultiSelect<CreditAgreement.Config.DeliverablePropertyDEPRECATED.PreUploadNotificationType>
                          initialSelections={_.map(
                            localFormProps.values._config_
                              .preUploadNotificationProcesses,
                            'notificationType',
                          )}
                          onApplyOptions={(notificationTypes) => {
                            localFormProps.setFieldValue(
                              '_config_.preUploadNotificationProcesses',
                              _.map(notificationTypes, (notificationType) => ({
                                notificationType,
                              })),
                            );
                          }}
                          options={formatSelectOptions(
                            [
                              ...CreditAgreement.Config
                                .DeliverablePropertyDEPRECATED
                                .PreUploadNotificationTypes,
                            ],
                            'sentence',
                          )}
                          titleText={
                            _.isEmpty(
                              localFormProps.values._config_
                                .preUploadNotificationProcesses,
                            )
                              ? 'Select notification types'
                              : `${
                                  localFormProps.values._config_
                                    .preUploadNotificationProcesses.length
                                } notification typ${
                                  localFormProps.values._config_
                                    .preUploadNotificationProcesses.length > 1
                                    ? 'es'
                                    : 'e'
                                } selected`
                          }
                          applyButtonText="Apply"
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    <Flex width="100%">
                      <FormFieldLabel
                        labelWidth="60"
                        label="Post-upload Notifications"
                      />
                      <InputGroup w="100%">
                        <IsolatedMultiSelect<CreditAgreement.Config.DeliverablePropertyDEPRECATED.PostUploadNotificationType>
                          initialSelections={_.map(
                            localFormProps.values._config_
                              .postUploadNotificationProcesses,
                            'notificationType',
                          )}
                          onApplyOptions={(notificationTypes) => {
                            localFormProps.setFieldValue(
                              '_config_.postUploadNotificationProcesses',
                              _.map(notificationTypes, (notificationType) => ({
                                notificationType,
                              })),
                            );
                          }}
                          options={formatSelectOptions(
                            [
                              ...CreditAgreement.Config
                                .DeliverablePropertyDEPRECATED
                                .PostUploadNotificationTypes,
                            ],
                            'sentence',
                          )}
                          titleText={
                            _.isEmpty(
                              localFormProps.values._config_
                                .postUploadNotificationProcesses,
                            )
                              ? 'Select notification types'
                              : `${
                                  localFormProps.values._config_
                                    .postUploadNotificationProcesses.length
                                } notification typ${
                                  localFormProps.values._config_
                                    .postUploadNotificationProcesses.length > 1
                                    ? 'es'
                                    : 'e'
                                } selected`
                          }
                          applyButtonText="Apply"
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                  </VStack>
                  <Flex justifyContent="center" mt="8">
                    <Button
                      type="submit"
                      aria-label="Done"
                      disabled={_.keys(formProps.errors).length > 0}
                    >
                      Done
                    </Button>
                  </Flex>
                </Form>
              );
            }}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
